import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Plus, Edit2, Trash2, Search, Tag, DollarSign, MapPin, Globe, Award, Heart, ExternalLink, Image, Video, X, Upload, Building2, ChevronLeft, ChevronRight } from 'lucide-react';

const LISTING_TYPES = [
  { value: '', label: 'All' },
  { value: 'sale', label: 'For Sale' },
  { value: 'lease', label: 'For Lease' },
  { value: 'stallion_service', label: 'Stallion Services' },
];

const BOARDING_OPTIONS = ['Pasture Board', 'Stall Board', 'Paddock', 'Run-In Shed', 'Self-Care', 'Full Care', 'Partial Care'];
const AMENITY_OPTIONS = ['Indoor Arena', 'Outdoor Arena', 'Round Pen', 'Wash Rack', 'Hot Walker', 'Trails', 'Turnout Paddocks', 'Tack Room', 'Feed Room', 'Cross-Country Course', 'Heated Barn', 'Fly System'];

const emptyForm = {
  title: '', description: '', horse_name: '', breed: '', age: '', price: '',
  listing_type: 'sale', contact_info: '', status: 'active',
  stallion_name: '', stud_fee: '', zip_code: '', service_radius_miles: '',
  website: '', show_earnings: '', sire: '', dam: '',
  sire_of_sire: '', dam_of_sire: '', sire_of_dam: '', dam_of_dam: '',
  bloodline_notes: '', registration_info: '',
  facility_name: '', address: '', city: '', state: '',
  boarding_types: '', amenities: '', monthly_rate: '', capacity: ''
};

const emptyFacilityForm = {
  ...emptyForm,
  listing_type: 'facility',
  title: '',
};

function MediaGallery({ photos, video, API, token }) {
  const [activeIdx, setActiveIdx] = useState(0);
  const allMedia = [...(photos || []), ...(video ? [video] : [])];
  if (allMedia.length === 0) return null;

  const current = allMedia[activeIdx] || allMedia[0];
  const isVideo = current?.media_type === 'video';
  const src = `${API}/files/${current.path}?auth=${token}`;

  return (
    <div className="mb-3">
      <div className="relative rounded-xl overflow-hidden bg-[#E9EFE8] aspect-[16/10]">
        {isVideo ? (
          <video src={src} controls className="w-full h-full object-cover" data-testid="listing-video" />
        ) : (
          <img src={src} alt="" className="w-full h-full object-cover" data-testid="listing-photo" />
        )}
        {allMedia.length > 1 && (
          <>
            <button onClick={() => setActiveIdx(i => (i - 1 + allMedia.length) % allMedia.length)} className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/40 text-white rounded-full p-1 hover:bg-black/60"><ChevronLeft size={16} /></button>
            <button onClick={() => setActiveIdx(i => (i + 1) % allMedia.length)} className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/40 text-white rounded-full p-1 hover:bg-black/60"><ChevronRight size={16} /></button>
            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1">
              {allMedia.map((_, i) => (
                <div key={i} className={`w-1.5 h-1.5 rounded-full ${i === activeIdx ? 'bg-white' : 'bg-white/50'}`} />
              ))}
            </div>
          </>
        )}
      </div>
      <div className="flex gap-1 mt-1.5 overflow-x-auto">
        {allMedia.map((m, i) => (
          <button key={m.id} onClick={() => setActiveIdx(i)} className={`flex-shrink-0 w-12 h-9 rounded-lg overflow-hidden border-2 ${i === activeIdx ? 'border-[#2D6A4F]' : 'border-transparent'}`}>
            {m.media_type === 'video' ? (
              <div className="w-full h-full bg-[#1B4332] flex items-center justify-center"><Video size={12} className="text-white" /></div>
            ) : (
              <img src={`${API}/files/${m.path}?auth=${token}`} alt="" className="w-full h-full object-cover" />
            )}
          </button>
        ))}
      </div>
    </div>
  );
}

function MediaUploader({ listingId, photos, video, onUpdate, API, authHeaders }) {
  const fileRef = useRef();
  const [uploading, setUploading] = useState(false);
  const photoCount = (photos || []).length;

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      await axios.post(`${API}/marketplace/${listingId}/media`, formData, {
        ...authHeaders(),
        headers: { ...authHeaders().headers, 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Media uploaded!');
      onUpdate();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleDelete = async (mediaId) => {
    try {
      await axios.delete(`${API}/marketplace/${listingId}/media/${mediaId}`, authHeaders());
      toast.success('Media removed');
      onUpdate();
    } catch (err) { toast.error('Failed to remove'); }
  };

  return (
    <div className="bg-white rounded-xl p-4 border border-[#D1E0D5]">
      <p className="text-sm font-semibold text-[#1B4332] mb-2 flex items-center gap-1"><Image size={14} /> Media ({photoCount}/5 photos{video ? ', 1 video' : ''})</p>
      <div className="flex gap-2 flex-wrap mb-3">
        {(photos || []).map(p => (
          <div key={p.id} className="relative w-16 h-16 rounded-lg overflow-hidden group">
            <div className="w-full h-full bg-[#E9EFE8] flex items-center justify-center text-xs text-[#4B6B58]"><Image size={16} /></div>
            <button onClick={() => handleDelete(p.id)} className="absolute top-0.5 right-0.5 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"><X size={10} /></button>
          </div>
        ))}
        {video && (
          <div className="relative w-16 h-16 rounded-lg overflow-hidden group bg-[#1B4332] flex items-center justify-center">
            <Video size={18} className="text-white" />
            <button onClick={() => handleDelete(video.id)} className="absolute top-0.5 right-0.5 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"><X size={10} /></button>
          </div>
        )}
      </div>
      <label className={`flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold cursor-pointer transition-colors ${uploading ? 'bg-[#D1E0D5] text-[#4B6B58]' : 'bg-[#E9EFE8] text-[#2D6A4F] hover:bg-[#D1E0D5]'}`}>
        <Upload size={13} /> {uploading ? 'Uploading...' : 'Add Photo or Video'}
        <input ref={fileRef} type="file" accept="image/*,video/mp4,video/webm,video/quicktime" onChange={handleUpload} className="hidden" disabled={uploading} data-testid="media-upload-input" />
      </label>
      <p className="text-[10px] text-[#4B6B58] mt-1.5">Max 5 photos + 1 video per listing. Photos: JPEG/PNG/WebP. Video: MP4/WebM.</p>
    </div>
  );
}

export default function MarketplacePage() {
  const { authHeaders, API, user, hasFullAccess, token } = useAuth();
  const [listings, setListings] = useState([]);
  const [myListings, setMyListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('');
  const [zipFilter, setZipFilter] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [editListing, setEditListing] = useState(null);
  const [tab, setTab] = useState('browse');
  const [form, setForm] = useState({ ...emptyForm });
  const [expandedPedigree, setExpandedPedigree] = useState(null);
  const [mediaManager, setMediaManager] = useState(null);

  // Facilities state
  const [facilities, setFacilities] = useState([]);
  const [facilityLoading, setFacilityLoading] = useState(false);
  const [showFacilityDialog, setShowFacilityDialog] = useState(false);
  const [editFacility, setEditFacility] = useState(null);
  const [facilityForm, setFacilityForm] = useState({ ...emptyFacilityForm });

  const fetchListings = async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.set('q', search);
      if (filterType) params.set('listing_type', filterType);
      if (zipFilter) params.set('zip_code', zipFilter);
      const res = await axios.get(`${API}/marketplace?${params}`, authHeaders());
      const all = res.data;
      setListings(all.filter(l => l.listing_type !== 'facility'));
      setFacilities(all.filter(l => l.listing_type === 'facility'));
    } catch (err) { /* silent */ }
    finally { setLoading(false); setFacilityLoading(false); }
  };

  const fetchMyListings = async () => {
    try {
      const res = await axios.get(`${API}/marketplace/my`, authHeaders());
      setMyListings(res.data);
    } catch (err) { /* silent */ }
  };

  const fetchFacilities = async () => {
    setFacilityLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('listing_type', 'facility');
      if (search) params.set('q', search);
      if (zipFilter) params.set('zip_code', zipFilter);
      const res = await axios.get(`${API}/marketplace?${params}`, authHeaders());
      setFacilities(res.data);
    } catch (err) { /* silent */ }
    finally { setFacilityLoading(false); }
  };

  useEffect(() => { fetchListings(); fetchMyListings(); }, []); // eslint-disable-line
  useEffect(() => {
    if (tab === 'facilities') { fetchFacilities(); }
    else { fetchListings(); }
  }, [search, filterType, zipFilter, tab]); // eslint-disable-line

  const handleSave = async (e) => {
    e.preventDefault();
    const data = {
      ...form,
      age: form.age ? parseInt(form.age) : null,
      price: parseFloat(form.price) || 0,
      stud_fee: form.stud_fee ? parseFloat(form.stud_fee) : null,
      service_radius_miles: form.service_radius_miles ? parseInt(form.service_radius_miles) : null,
      monthly_rate: form.monthly_rate ? parseFloat(form.monthly_rate) : null,
      capacity: form.capacity ? parseInt(form.capacity) : null,
    };
    try {
      if (editListing) {
        await axios.put(`${API}/marketplace/${editListing.id}`, data, authHeaders());
        toast.success('Listing updated!');
      } else {
        const res = await axios.post(`${API}/marketplace`, data, authHeaders());
        toast.success('Listing created!');
        // Open media manager for new listing
        if (res.data?.id) setMediaManager(res.data.id);
      }
      setShowDialog(false); setEditListing(null);
      setForm({ ...emptyForm });
      fetchListings(); fetchMyListings();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
  };

  const handleFacilitySave = async (e) => {
    e.preventDefault();
    const data = {
      ...facilityForm,
      listing_type: 'facility',
      monthly_rate: facilityForm.monthly_rate ? parseFloat(facilityForm.monthly_rate) : null,
      capacity: facilityForm.capacity ? parseInt(facilityForm.capacity) : null,
      price: 0, age: null, stud_fee: null, service_radius_miles: null,
    };
    try {
      if (editFacility) {
        await axios.put(`${API}/marketplace/${editFacility.id}`, data, authHeaders());
        toast.success('Facility updated!');
      } else {
        const res = await axios.post(`${API}/marketplace`, data, authHeaders());
        toast.success('Facility listed!');
        if (res.data?.id) setMediaManager(res.data.id);
      }
      setShowFacilityDialog(false); setEditFacility(null);
      setFacilityForm({ ...emptyFacilityForm });
      fetchFacilities(); fetchMyListings();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this listing?')) return;
    try {
      await axios.delete(`${API}/marketplace/${id}`, authHeaders());
      toast.success('Listing deleted');
      fetchListings(); fetchMyListings(); fetchFacilities();
    } catch (err) { toast.error('Failed to delete'); }
  };

  const openAdd = () => {
    if (!hasFullAccess) { toast.error('Subscribe to create listings.'); return; }
    setEditListing(null);
    setForm({ ...emptyForm });
    setShowDialog(true);
  };

  const openAddFacility = () => {
    if (!hasFullAccess) { toast.error('Subscribe to create listings.'); return; }
    setEditFacility(null);
    setFacilityForm({ ...emptyFacilityForm });
    setShowFacilityDialog(true);
  };

  const openEdit = (l) => {
    if (l.listing_type === 'facility') {
      setEditFacility(l);
      setFacilityForm({
        ...emptyFacilityForm,
        title: l.title || '', description: l.description || '', contact_info: l.contact_info || '',
        facility_name: l.facility_name || '', address: l.address || '', city: l.city || '',
        state: l.state || '', zip_code: l.zip_code || '', website: l.website || '',
        boarding_types: l.boarding_types || '', amenities: l.amenities || '',
        monthly_rate: l.monthly_rate?.toString() || '', capacity: l.capacity?.toString() || '',
      });
      setShowFacilityDialog(true);
      return;
    }
    setEditListing(l);
    setForm({
      title: l.title || '', description: l.description || '', horse_name: l.horse_name || '',
      breed: l.breed || '', age: l.age?.toString() || '', price: l.price?.toString() || '',
      listing_type: l.listing_type || 'sale', contact_info: l.contact_info || '', status: l.status || 'active',
      stallion_name: l.stallion_name || '', stud_fee: l.stud_fee?.toString() || '',
      zip_code: l.zip_code || '', service_radius_miles: l.service_radius_miles?.toString() || '',
      website: l.website || '', show_earnings: l.show_earnings || '',
      sire: l.sire || '', dam: l.dam || '',
      sire_of_sire: l.sire_of_sire || '', dam_of_sire: l.dam_of_sire || '',
      sire_of_dam: l.sire_of_dam || '', dam_of_dam: l.dam_of_dam || '',
      bloodline_notes: l.bloodline_notes || '', registration_info: l.registration_info || '',
      facility_name: '', address: '', city: '', state: '',
      boarding_types: '', amenities: '', monthly_rate: '', capacity: '',
    });
    setShowDialog(true);
  };

  const isStallion = form.listing_type === 'stallion_service';
  const displayListings = tab === 'my' ? myListings.filter(l => l.listing_type !== 'facility') : listings;

  const ListingCard = ({ l }) => {
    const isStallionListing = l.listing_type === 'stallion_service';
    const hasPedigree = l.sire || l.dam;
    const isExpanded = expandedPedigree === l.id;
    const hasMedia = (l.photos && l.photos.length > 0) || l.video;

    return (
      <div className="bg-white rounded-[2rem] overflow-hidden card-hover shadow-[0_8px_30px_rgb(27,67,50,0.06)]" data-testid={`listing-${l.id}`}>
        <div className="p-6">
          {hasMedia && <MediaGallery photos={l.photos} video={l.video} API={API} token={token} />}
          <div className="flex items-start justify-between mb-3">
            <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${
              isStallionListing ? 'bg-[#F3E8FF] text-[#7C3AED]' :
              l.listing_type === 'sale' ? 'bg-[#E9EFE8] text-[#2D6A4F]' : 'bg-[#FEF3C7] text-[#92400E]'
            }`}>
              {isStallionListing ? 'Stallion Service' : `For ${l.listing_type}`}
            </span>
            {l.user_id === user?.id && (
              <div className="flex gap-1">
                <button onClick={() => openEdit(l)} data-testid={`edit-listing-${l.id}`} className="p-1.5 rounded-full hover:bg-[#E9EFE8] text-[#4B6B58]"><Edit2 size={14} /></button>
                <button onClick={() => setMediaManager(mediaManager === l.id ? null : l.id)} data-testid={`media-listing-${l.id}`} className="p-1.5 rounded-full hover:bg-[#E9EFE8] text-[#4B6B58]"><Image size={14} /></button>
                <button onClick={() => handleDelete(l.id)} data-testid={`delete-listing-${l.id}`} className="p-1.5 rounded-full hover:bg-red-50 text-red-400"><Trash2 size={14} /></button>
              </div>
            )}
          </div>

          <h3 className="font-['Cormorant_Garamond'] text-lg font-bold text-[#1B4332] mb-1">{l.title}</h3>

          {isStallionListing ? (
            <>
              {l.stallion_name && <p className="text-sm font-semibold text-[#4B6B58] mb-1">{l.stallion_name} {l.breed && `- ${l.breed}`}</p>}
              {l.registration_info && <p className="text-xs text-[#4B6B58] mb-2">Reg: {l.registration_info}</p>}
              {l.description && <p className="text-sm text-[#4B6B58] mb-3 line-clamp-2">{l.description}</p>}
              <div className="grid grid-cols-2 gap-2 mb-3">
                {l.stud_fee != null && (
                  <div className="bg-[#F3E8FF]/30 rounded-lg px-3 py-2">
                    <p className="text-[10px] text-[#7C3AED] font-semibold uppercase">Stud Fee</p>
                    <p className="text-sm font-bold text-[#1B4332]">${l.stud_fee.toLocaleString()}</p>
                  </div>
                )}
                {l.show_earnings && (
                  <div className="bg-[#FEF3C7]/30 rounded-lg px-3 py-2">
                    <p className="text-[10px] text-[#92400E] font-semibold uppercase">Show Earnings</p>
                    <p className="text-sm font-bold text-[#1B4332]">{l.show_earnings}</p>
                  </div>
                )}
              </div>
              {hasPedigree && (
                <div className="mb-3">
                  <button onClick={() => setExpandedPedigree(isExpanded ? null : l.id)} className="text-xs font-semibold text-[#7C3AED] hover:underline flex items-center gap-1" data-testid={`pedigree-toggle-${l.id}`}>
                    <Heart size={12} /> {isExpanded ? 'Hide' : 'View'} Pedigree
                  </button>
                  {isExpanded && (
                    <div className="mt-2 bg-[#F9F7F3] rounded-xl p-3 text-xs space-y-1" data-testid={`pedigree-${l.id}`}>
                      <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                        {l.sire && <p><span className="font-semibold text-[#1B4332]">Sire:</span> <span className="text-[#4B6B58]">{l.sire}</span></p>}
                        {l.dam && <p><span className="font-semibold text-[#1B4332]">Dam:</span> <span className="text-[#4B6B58]">{l.dam}</span></p>}
                        {l.sire_of_sire && <p><span className="font-semibold text-[#1B4332]">Sire's Sire:</span> <span className="text-[#4B6B58]">{l.sire_of_sire}</span></p>}
                        {l.dam_of_sire && <p><span className="font-semibold text-[#1B4332]">Sire's Dam:</span> <span className="text-[#4B6B58]">{l.dam_of_sire}</span></p>}
                        {l.sire_of_dam && <p><span className="font-semibold text-[#1B4332]">Dam's Sire:</span> <span className="text-[#4B6B58]">{l.sire_of_dam}</span></p>}
                        {l.dam_of_dam && <p><span className="font-semibold text-[#1B4332]">Dam's Dam:</span> <span className="text-[#4B6B58]">{l.dam_of_dam}</span></p>}
                      </div>
                      {l.bloodline_notes && <p className="mt-1 pt-1 border-t border-[#D1E0D5] text-[#4B6B58]">{l.bloodline_notes}</p>}
                    </div>
                  )}
                </div>
              )}
              <div className="flex items-center gap-3 flex-wrap text-xs text-[#4B6B58]">
                {l.zip_code && <span className="flex items-center gap-1"><MapPin size={10} /> {l.zip_code} {l.service_radius_miles && `(${l.service_radius_miles} mi)`}</span>}
                {l.website && <a href={l.website.startsWith('http') ? l.website : `https://${l.website}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-[#7C3AED] hover:underline"><Globe size={10} /> Website <ExternalLink size={9} /></a>}
              </div>
            </>
          ) : (
            <>
              {l.horse_name && <p className="text-sm font-medium text-[#4B6B58] mb-2">{l.horse_name} {l.breed && `- ${l.breed}`} {l.age && `(${l.age} yrs)`}</p>}
              {l.description && <p className="text-sm text-[#4B6B58] mb-3 line-clamp-2">{l.description}</p>}
              <div className="flex items-center justify-between">
                <span className="font-['Cormorant_Garamond'] text-xl font-bold text-[#1B4332]">
                  {l.price > 0 ? `$${l.price.toLocaleString()}` : 'Contact for price'}
                </span>
              </div>
            </>
          )}

          {/* Media Manager (owner only) */}
          {mediaManager === l.id && l.user_id === user?.id && (
            <div className="mt-3">
              <MediaUploader listingId={l.id} photos={l.photos} video={l.video} onUpdate={() => { fetchListings(); fetchMyListings(); }} API={API} authHeaders={authHeaders} />
            </div>
          )}

          <div className="flex items-center justify-between mt-3 pt-3 border-t border-[#E9EFE8]">
            <span className="text-xs text-[#4B6B58]">by {l.seller_name}</span>
            {l.contact_info && <span className="text-xs text-[#4B6B58] flex items-center gap-1"><MapPin size={10} /> {l.contact_info}</span>}
          </div>
        </div>
      </div>
    );
  };

  const FacilityCard = ({ l }) => {
    const hasMedia = (l.photos && l.photos.length > 0) || l.video;
    const boardingList = l.boarding_types ? l.boarding_types.split(',').map(s => s.trim()).filter(Boolean) : [];
    const amenityList = l.amenities ? l.amenities.split(',').map(s => s.trim()).filter(Boolean) : [];

    return (
      <div className="bg-white rounded-[2rem] overflow-hidden card-hover shadow-[0_8px_30px_rgb(27,67,50,0.06)]" data-testid={`facility-${l.id}`}>
        <div className="p-6">
          {hasMedia && <MediaGallery photos={l.photos} video={l.video} API={API} token={token} />}
          <div className="flex items-start justify-between mb-3">
            <span className="px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-[#DBEAFE] text-[#1D4ED8]">
              Equine Facility
            </span>
            {l.user_id === user?.id && (
              <div className="flex gap-1">
                <button onClick={() => openEdit(l)} className="p-1.5 rounded-full hover:bg-[#E9EFE8] text-[#4B6B58]"><Edit2 size={14} /></button>
                <button onClick={() => setMediaManager(mediaManager === l.id ? null : l.id)} className="p-1.5 rounded-full hover:bg-[#E9EFE8] text-[#4B6B58]"><Image size={14} /></button>
                <button onClick={() => handleDelete(l.id)} className="p-1.5 rounded-full hover:bg-red-50 text-red-400"><Trash2 size={14} /></button>
              </div>
            )}
          </div>

          <h3 className="font-['Cormorant_Garamond'] text-lg font-bold text-[#1B4332] mb-0.5">{l.title || l.facility_name}</h3>
          {l.facility_name && l.title !== l.facility_name && <p className="text-sm font-medium text-[#4B6B58] mb-1">{l.facility_name}</p>}
          {l.description && <p className="text-sm text-[#4B6B58] mb-3 line-clamp-3">{l.description}</p>}

          <div className="grid grid-cols-2 gap-2 mb-3">
            {l.monthly_rate != null && l.monthly_rate > 0 && (
              <div className="bg-[#DBEAFE]/30 rounded-lg px-3 py-2">
                <p className="text-[10px] text-[#1D4ED8] font-semibold uppercase">Starting At</p>
                <p className="text-sm font-bold text-[#1B4332]">${l.monthly_rate.toLocaleString()}/mo</p>
              </div>
            )}
            {l.capacity && (
              <div className="bg-[#E9EFE8]/50 rounded-lg px-3 py-2">
                <p className="text-[10px] text-[#2D6A4F] font-semibold uppercase">Capacity</p>
                <p className="text-sm font-bold text-[#1B4332]">{l.capacity} horses</p>
              </div>
            )}
          </div>

          {boardingList.length > 0 && (
            <div className="mb-2">
              <p className="text-[10px] font-semibold text-[#1B4332] uppercase mb-1">Boarding Types</p>
              <div className="flex flex-wrap gap-1">
                {boardingList.map(b => (
                  <span key={b} className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-[#E9EFE8] text-[#2D6A4F]">{b}</span>
                ))}
              </div>
            </div>
          )}

          {amenityList.length > 0 && (
            <div className="mb-3">
              <p className="text-[10px] font-semibold text-[#1B4332] uppercase mb-1">Amenities</p>
              <div className="flex flex-wrap gap-1">
                {amenityList.map(a => (
                  <span key={a} className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-[#FEF3C7] text-[#92400E]">{a}</span>
                ))}
              </div>
            </div>
          )}

          {/* Media Manager (owner only) */}
          {mediaManager === l.id && l.user_id === user?.id && (
            <div className="mt-3">
              <MediaUploader listingId={l.id} photos={l.photos} video={l.video} onUpdate={() => { fetchFacilities(); fetchMyListings(); }} API={API} authHeaders={authHeaders} />
            </div>
          )}

          <div className="space-y-1 text-xs text-[#4B6B58] mt-2">
            {(l.address || l.city || l.state || l.zip_code) && (
              <p className="flex items-center gap-1.5"><MapPin size={12} /> {[l.address, l.city, l.state, l.zip_code].filter(Boolean).join(', ')}</p>
            )}
            {l.contact_info && <p className="flex items-center gap-1.5"><Building2 size={12} /> {l.contact_info}</p>}
            {l.website && (
              <a href={l.website.startsWith('http') ? l.website : `https://${l.website}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-[#1D4ED8] hover:underline">
                <Globe size={12} /> Website <ExternalLink size={10} />
              </a>
            )}
          </div>
          <div className="mt-3 pt-3 border-t border-[#E9EFE8]">
            <span className="text-xs text-[#4B6B58]">Listed by {l.seller_name}</span>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#F2F7F4] texture-grain">
      <Navbar />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 animate-fade-in">
          <div>
            <h1 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl font-bold text-[#1B4332]" data-testid="marketplace-title">
              Horse Marketplace
            </h1>
            <p className="text-[#4B6B58] mt-1">Buy, sell, lease horses, find stallion services & equine facilities.</p>
          </div>
          {tab === 'facilities' ? (
            <button onClick={openAddFacility} data-testid="create-facility-button" className="flex items-center gap-2 px-6 py-3 rounded-full bg-[#1D4ED8] text-white font-semibold btn-lift shadow-[0_4px_12px_rgb(29,78,216,0.15)] hover:bg-[#1E40AF]">
              <Building2 size={18} /> List Facility
            </button>
          ) : (
            <button onClick={openAdd} data-testid="create-listing-button" className="flex items-center gap-2 px-6 py-3 rounded-full bg-[#1B4332] text-white font-semibold btn-lift shadow-[0_4px_12px_rgb(27,67,50,0.15)] hover:bg-[#2D6A4F]">
              <Plus size={18} /> Post Listing
            </button>
          )}
        </div>

        {/* Tabs & Filters */}
        <div className="flex flex-wrap items-center gap-3 mb-6 animate-fade-in-delay-1">
          <div className="bg-white rounded-full p-1 flex gap-1 shadow-[0_2px_8px_rgb(27,67,50,0.04)]">
            <button onClick={() => { setTab('browse'); setFilterType(''); }} data-testid="tab-browse" className={`px-4 py-2 rounded-full text-sm font-medium ${tab === 'browse' ? 'bg-[#1B4332] text-white' : 'text-[#4B6B58]'}`}>Browse All</button>
            <button onClick={() => { setTab('facilities'); setFilterType(''); }} data-testid="tab-facilities" className={`px-4 py-2 rounded-full text-sm font-medium ${tab === 'facilities' ? 'bg-[#1D4ED8] text-white' : 'text-[#4B6B58]'}`}>Facilities</button>
            <button onClick={() => { setTab('my'); setFilterType(''); }} data-testid="tab-my-listings" className={`px-4 py-2 rounded-full text-sm font-medium ${tab === 'my' ? 'bg-[#1B4332] text-white' : 'text-[#4B6B58]'}`}>My Listings</button>
          </div>
          {tab !== 'my' && (
            <>
              <div className="relative flex-1 min-w-[180px]">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4B6B58]" />
                <input value={search} onChange={e => setSearch(e.target.value)} placeholder={tab === 'facilities' ? "Search facilities, amenities..." : "Search horses, stallions, bloodlines..."} data-testid="marketplace-search" className="stable-input w-full pl-9 py-2 text-sm" />
              </div>
              <div className="relative min-w-[100px]">
                <MapPin size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4B6B58]" />
                <input value={zipFilter} onChange={e => setZipFilter(e.target.value)} placeholder="Zip code" data-testid="marketplace-zip-filter" className="stable-input w-full pl-8 py-2 text-sm" maxLength={5} />
              </div>
              {tab === 'browse' && (
                <div className="flex gap-2 flex-wrap">
                  {LISTING_TYPES.map(t => (
                    <button key={t.value} onClick={() => setFilterType(t.value)} className={`px-3 py-1.5 rounded-full text-xs font-medium ${filterType === t.value ? 'bg-[#1B4332] text-white' : 'bg-white text-[#4B6B58]'}`}>
                      {t.label}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {/* Content based on tab */}
        {tab === 'facilities' ? (
          /* Facilities Grid */
          facilityLoading ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">{[1,2,3].map(i => <div key={i} className="bg-white rounded-[2rem] h-52 animate-pulse" />)}</div>
          ) : facilities.length === 0 ? (
            <div className="bg-white rounded-[2rem] p-12 text-center shadow-[0_8px_30px_rgb(27,67,50,0.06)]" data-testid="no-facilities">
              <Building2 size={32} className="text-[#4B6B58]/40 mx-auto mb-3" />
              <h3 className="font-['Cormorant_Garamond'] text-xl font-bold text-[#1B4332] mb-2">No Facilities Listed</h3>
              <p className="text-sm text-[#4B6B58]">Be the first to list an equine boarding or training facility!</p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {facilities.map(l => <FacilityCard key={l.id} l={l} />)}
            </div>
          )
        ) : (
          /* Regular Listings Grid */
          loading ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">{[1,2,3].map(i => <div key={i} className="bg-white rounded-[2rem] h-52 animate-pulse" />)}</div>
          ) : displayListings.length === 0 ? (
            <div className="bg-white rounded-[2rem] p-12 text-center shadow-[0_8px_30px_rgb(27,67,50,0.06)]" data-testid="no-listings">
              <Tag size={32} className="text-[#4B6B58]/40 mx-auto mb-3" />
              <h3 className="font-['Cormorant_Garamond'] text-xl font-bold text-[#1B4332] mb-2">{tab === 'my' ? 'No Listings Yet' : 'No Listings Found'}</h3>
              <p className="text-sm text-[#4B6B58]">{tab === 'my' ? 'Create your first listing.' : 'Try a different search.'}</p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {displayListings.map(l => <ListingCard key={l.id} l={l} />)}
            </div>
          )
        )}
      </main>

      {/* Create/Edit Listing Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="bg-[#F2F7F4] border-[#D1E0D5] rounded-[1.5rem] max-w-lg max-h-[85vh] overflow-y-auto" data-testid="listing-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Cormorant_Garamond'] text-2xl font-bold text-[#1B4332]">{editListing ? 'Edit Listing' : 'Post New Listing'}</DialogTitle>
            <DialogDescription className="text-[#4B6B58]">Fill in the details below.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div><label className="block text-sm font-medium text-[#122A1F] mb-1">Type *</label>
                <select value={form.listing_type} onChange={e => setForm({...form, listing_type: e.target.value})} data-testid="listing-type-select" className="stable-input w-full">
                  <option value="sale">For Sale</option>
                  <option value="lease">For Lease</option>
                  <option value="stallion_service">Stallion Service</option>
                </select>
              </div>
              <div><label className="block text-sm font-medium text-[#122A1F] mb-1">Title *</label>
                <input value={form.title} onChange={e => setForm({...form, title: e.target.value})} required data-testid="listing-title-input" className="stable-input w-full" placeholder={isStallion ? 'e.g., Elite AQHA Stallion' : 'e.g., Beautiful Bay Mare'} />
              </div>
            </div>

            {isStallion ? (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div><label className="block text-sm font-medium text-[#122A1F] mb-1">Stallion Name *</label>
                    <input value={form.stallion_name} onChange={e => setForm({...form, stallion_name: e.target.value})} data-testid="stallion-name-input" className="stable-input w-full" placeholder="Registered name" />
                  </div>
                  <div><label className="block text-sm font-medium text-[#122A1F] mb-1">Breed</label>
                    <input value={form.breed} onChange={e => setForm({...form, breed: e.target.value})} data-testid="listing-breed-input" className="stable-input w-full" placeholder="e.g., AQHA" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div><label className="block text-sm font-medium text-[#122A1F] mb-1">Stud Fee ($)</label>
                    <input type="number" value={form.stud_fee} onChange={e => setForm({...form, stud_fee: e.target.value})} data-testid="stud-fee-input" className="stable-input w-full" placeholder="0" />
                  </div>
                  <div><label className="block text-sm font-medium text-[#122A1F] mb-1">Show Earnings</label>
                    <input value={form.show_earnings} onChange={e => setForm({...form, show_earnings: e.target.value})} data-testid="show-earnings-input" className="stable-input w-full" placeholder="e.g., $125,000 NRHA" />
                  </div>
                </div>
                <div><label className="block text-sm font-medium text-[#122A1F] mb-1">Registration Info</label>
                  <input value={form.registration_info} onChange={e => setForm({...form, registration_info: e.target.value})} data-testid="stallion-reg-input" className="stable-input w-full" placeholder="e.g., AQHA #12345678" />
                </div>
                <div className="bg-white rounded-xl p-4 border border-[#D1E0D5]">
                  <p className="text-sm font-semibold text-[#1B4332] mb-3 flex items-center gap-1"><Heart size={14} /> Extended Pedigree</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div><label className="block text-xs font-medium text-[#4B6B58] mb-1">Sire</label><input value={form.sire} onChange={e => setForm({...form, sire: e.target.value})} data-testid="sire-input" className="stable-input w-full text-sm" /></div>
                    <div><label className="block text-xs font-medium text-[#4B6B58] mb-1">Dam</label><input value={form.dam} onChange={e => setForm({...form, dam: e.target.value})} data-testid="dam-input" className="stable-input w-full text-sm" /></div>
                    <div><label className="block text-xs font-medium text-[#4B6B58] mb-1">Sire's Sire</label><input value={form.sire_of_sire} onChange={e => setForm({...form, sire_of_sire: e.target.value})} data-testid="sire-sire-input" className="stable-input w-full text-sm" /></div>
                    <div><label className="block text-xs font-medium text-[#4B6B58] mb-1">Sire's Dam</label><input value={form.dam_of_sire} onChange={e => setForm({...form, dam_of_sire: e.target.value})} data-testid="dam-sire-input" className="stable-input w-full text-sm" /></div>
                    <div><label className="block text-xs font-medium text-[#4B6B58] mb-1">Dam's Sire</label><input value={form.sire_of_dam} onChange={e => setForm({...form, sire_of_dam: e.target.value})} data-testid="sire-dam-input" className="stable-input w-full text-sm" /></div>
                    <div><label className="block text-xs font-medium text-[#4B6B58] mb-1">Dam's Dam</label><input value={form.dam_of_dam} onChange={e => setForm({...form, dam_of_dam: e.target.value})} data-testid="dam-dam-input" className="stable-input w-full text-sm" /></div>
                  </div>
                  <div className="mt-3"><label className="block text-xs font-medium text-[#4B6B58] mb-1">Bloodline Notes</label>
                    <textarea value={form.bloodline_notes} onChange={e => setForm({...form, bloodline_notes: e.target.value})} data-testid="bloodline-notes-input" className="stable-input w-full text-sm" rows={2} placeholder="Additional bloodline details..." />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div><label className="block text-sm font-medium text-[#122A1F] mb-1">Zip Code</label><input value={form.zip_code} onChange={e => setForm({...form, zip_code: e.target.value})} data-testid="zip-code-input" className="stable-input w-full" maxLength={5} placeholder="12345" /></div>
                  <div><label className="block text-sm font-medium text-[#122A1F] mb-1">Radius (mi)</label><input type="number" value={form.service_radius_miles} onChange={e => setForm({...form, service_radius_miles: e.target.value})} data-testid="radius-input" className="stable-input w-full" placeholder="50" /></div>
                  <div><label className="block text-sm font-medium text-[#122A1F] mb-1">Website</label><input value={form.website} onChange={e => setForm({...form, website: e.target.value})} data-testid="website-input" className="stable-input w-full" placeholder="www.example.com" /></div>
                </div>
              </>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div><label className="block text-sm font-medium text-[#122A1F] mb-1">Price ($)</label><input type="number" value={form.price} onChange={e => setForm({...form, price: e.target.value})} data-testid="listing-price-input" className="stable-input w-full" placeholder="0" /></div>
                  <div><label className="block text-sm font-medium text-[#122A1F] mb-1">Horse Name</label><input value={form.horse_name} onChange={e => setForm({...form, horse_name: e.target.value})} data-testid="listing-horse-input" className="stable-input w-full" /></div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div><label className="block text-sm font-medium text-[#122A1F] mb-1">Breed</label><input value={form.breed} onChange={e => setForm({...form, breed: e.target.value})} data-testid="listing-breed-input" className="stable-input w-full" /></div>
                  <div><label className="block text-sm font-medium text-[#122A1F] mb-1">Age</label><input type="number" value={form.age} onChange={e => setForm({...form, age: e.target.value})} data-testid="listing-age-input" className="stable-input w-full" /></div>
                </div>
              </>
            )}

            <div><label className="block text-sm font-medium text-[#122A1F] mb-1">Description</label>
              <textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} data-testid="listing-desc-input" className="stable-input w-full" rows={3} />
            </div>
            <div><label className="block text-sm font-medium text-[#122A1F] mb-1">Contact Info</label>
              <input value={form.contact_info} onChange={e => setForm({...form, contact_info: e.target.value})} data-testid="listing-contact-input" className="stable-input w-full" placeholder="Phone, email, or location" />
            </div>
            <button type="submit" data-testid="listing-save-button" className="w-full py-3 rounded-full bg-[#1B4332] text-white font-semibold btn-lift hover:bg-[#2D6A4F]">{editListing ? 'Update' : 'Post Listing'}</button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Facility Dialog */}
      <Dialog open={showFacilityDialog} onOpenChange={setShowFacilityDialog}>
        <DialogContent className="bg-[#F2F7F4] border-[#D1E0D5] rounded-[1.5rem] max-w-lg max-h-[85vh] overflow-y-auto" data-testid="facility-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Cormorant_Garamond'] text-2xl font-bold text-[#1B4332]">{editFacility ? 'Edit Facility' : 'List an Equine Facility'}</DialogTitle>
            <DialogDescription className="text-[#4B6B58]">Share details about a boarding or training facility.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleFacilitySave} className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div><label className="block text-sm font-medium text-[#122A1F] mb-1">Facility Name *</label>
                <input value={facilityForm.facility_name} onChange={e => setFacilityForm({...facilityForm, facility_name: e.target.value, title: e.target.value})} required data-testid="facility-name-input" className="stable-input w-full" placeholder="e.g., Green Meadows Stables" />
              </div>
              <div><label className="block text-sm font-medium text-[#122A1F] mb-1">Monthly Rate ($)</label>
                <input type="number" value={facilityForm.monthly_rate} onChange={e => setFacilityForm({...facilityForm, monthly_rate: e.target.value})} data-testid="facility-rate-input" className="stable-input w-full" placeholder="Starting from..." />
              </div>
            </div>
            <div><label className="block text-sm font-medium text-[#122A1F] mb-1">Description</label>
              <textarea value={facilityForm.description} onChange={e => setFacilityForm({...facilityForm, description: e.target.value})} data-testid="facility-desc-input" className="stable-input w-full" rows={3} placeholder="Describe the facility, what makes it special..." />
            </div>
            <div><label className="block text-sm font-medium text-[#122A1F] mb-1">Address</label>
              <input value={facilityForm.address} onChange={e => setFacilityForm({...facilityForm, address: e.target.value})} data-testid="facility-address-input" className="stable-input w-full" placeholder="Street address" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><label className="block text-sm font-medium text-[#122A1F] mb-1">City</label>
                <input value={facilityForm.city} onChange={e => setFacilityForm({...facilityForm, city: e.target.value})} data-testid="facility-city-input" className="stable-input w-full" />
              </div>
              <div><label className="block text-sm font-medium text-[#122A1F] mb-1">State</label>
                <input value={facilityForm.state} onChange={e => setFacilityForm({...facilityForm, state: e.target.value})} data-testid="facility-state-input" className="stable-input w-full" maxLength={2} placeholder="OR" />
              </div>
              <div><label className="block text-sm font-medium text-[#122A1F] mb-1">Zip Code</label>
                <input value={facilityForm.zip_code} onChange={e => setFacilityForm({...facilityForm, zip_code: e.target.value})} data-testid="facility-zip-input" className="stable-input w-full" maxLength={5} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><label className="block text-sm font-medium text-[#122A1F] mb-1">Capacity (horses)</label>
                <input type="number" value={facilityForm.capacity} onChange={e => setFacilityForm({...facilityForm, capacity: e.target.value})} data-testid="facility-capacity-input" className="stable-input w-full" placeholder="e.g., 30" />
              </div>
              <div><label className="block text-sm font-medium text-[#122A1F] mb-1">Website</label>
                <input value={facilityForm.website} onChange={e => setFacilityForm({...facilityForm, website: e.target.value})} data-testid="facility-website-input" className="stable-input w-full" placeholder="www.example.com" />
              </div>
            </div>

            {/* Boarding Types */}
            <div>
              <label className="block text-sm font-medium text-[#122A1F] mb-2">Boarding Types</label>
              <div className="flex flex-wrap gap-2">
                {BOARDING_OPTIONS.map(opt => {
                  const selected = facilityForm.boarding_types.split(',').map(s => s.trim()).includes(opt);
                  return (
                    <button key={opt} type="button" onClick={() => {
                      const current = facilityForm.boarding_types.split(',').map(s => s.trim()).filter(Boolean);
                      const updated = selected ? current.filter(c => c !== opt) : [...current, opt];
                      setFacilityForm({...facilityForm, boarding_types: updated.join(', ')});
                    }} className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${selected ? 'bg-[#2D6A4F] text-white' : 'bg-[#E9EFE8] text-[#4B6B58] hover:bg-[#D1E0D5]'}`}>
                      {opt}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Amenities */}
            <div>
              <label className="block text-sm font-medium text-[#122A1F] mb-2">Amenities</label>
              <div className="flex flex-wrap gap-2">
                {AMENITY_OPTIONS.map(opt => {
                  const selected = facilityForm.amenities.split(',').map(s => s.trim()).includes(opt);
                  return (
                    <button key={opt} type="button" onClick={() => {
                      const current = facilityForm.amenities.split(',').map(s => s.trim()).filter(Boolean);
                      const updated = selected ? current.filter(c => c !== opt) : [...current, opt];
                      setFacilityForm({...facilityForm, amenities: updated.join(', ')});
                    }} className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${selected ? 'bg-[#92400E] text-white' : 'bg-[#FEF3C7] text-[#92400E] hover:bg-[#FDE68A]'}`}>
                      {opt}
                    </button>
                  );
                })}
              </div>
            </div>

            <div><label className="block text-sm font-medium text-[#122A1F] mb-1">Contact Info</label>
              <input value={facilityForm.contact_info} onChange={e => setFacilityForm({...facilityForm, contact_info: e.target.value})} data-testid="facility-contact-input" className="stable-input w-full" placeholder="Phone, email, or other" />
            </div>
            <button type="submit" data-testid="facility-save-button" className="w-full py-3 rounded-full bg-[#1D4ED8] text-white font-semibold btn-lift hover:bg-[#1E40AF]">{editFacility ? 'Update Facility' : 'List Facility'}</button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
